import { defineStore } from "pinia";
import ChatService from "@/services/ChatService";
import { reactive, toRefs, computed } from "vue";
import { Chat, Message, ChatStoreState } from "@/features/chat/chat.types";
import { useAuth } from "@/services/AuthService";
import { SchultenMedia } from "@/services/DataService";
import { formatMessageTime } from "@/utils/messageDates";

// Outcome of a send. A failure never touches `state.error`: that field replaces the
// whole conversation and chat list. The caller shows `error` in a toast; "" means
// it has no message worth showing.
type SendResult = { message: Message; error?: undefined } | { message?: undefined; error: string }

export const useChatStore = defineStore('chat', () => {
    // Reactive state for chats, messages, and real-time features
    const state = reactive<ChatStoreState>({
        chats: [],
        currentChat: null,
        messages: [],
        isLoading: false,
        hasFetched: false,
        error: null,
        isTyping: false,
        typingUsers: new Set(),
        seenMessageIds: new Set<number>(),
    });

    // De aanvraag die nu loopt, met het gesprek en de pagina waar hij bij hoort,
    // zodat een tweede aanroep weet of dit zijn antwoord is of dat van een ander.
    let pageRequest: { key: string; promise: Promise<void> } | null = null

    // Pagination state for current conversation
    const messagePagination = reactive({
        currentPage: 1,
        lastPage: 1,
        isLoadingPage: false,
        hasMore: true,
    })

    // The list holds either the newest pages (page mode), or a stretch around a quoted
    // message that grows by id from both ends (COACHIO-2032). `cursorPaging` stays on
    // once the stretch reaches now, so older loads keep going by id and never overlap
    // a page. `browsing` = the newest messages are not in the list: a live message is
    // counted in `missed` instead of being appended out of order. Those payloads are
    // never shown (an edit or reaction may have changed them since); the stretch from
    // the server that reaches now brings them.
    const windowState = reactive({
        cursorPaging: false,
        browsing: false,
        // Ids of the live messages counted while browsing.
        missed: [] as number[],
    })
    // Bumped whenever the list is replaced; a request that started before drops its answer.
    let listGeneration = 0
    // Live messages for the open chat, in arrival order, so a window request can tell
    // which came in while it was loading (its answer may not have them).
    let liveArrivals: number[] = []

    // Debounce controller for typing API
    let typingAbortController: AbortController | null = null
    let typingDebounceTimer: number | undefined

    // Get current user ID from auth service
    const { user } = useAuth();
    const currentUserId = computed(() => (user.value as any)?.id);

    // Computed properties
    const unreadChatsCount = computed(() =>
        state.chats.reduce((count, chat) => count + chat.unread_count, 0)
    );

    // Sorting moved to ChatPage to avoid pre-fetch ordering

    // Fetch chats and update the store's state
    async function fetchChats() {
        // Don't fetch chats if user is not authenticated
        if (!SchultenMedia.Authenticated) {
            return;
        }

        state.isLoading = true;
        state.error = null;

        try {
            const response = await ChatService.getChats();

            if (response.success && response.data) {
                state.chats = Array.isArray(response.data) ? response.data : [];
            } else {
                state.error = response.message;
                state.chats = [];
            }
        } catch (err) {
            state.error = err;
            state.chats = [];
        } finally {
            state.isLoading = false;
            state.hasFetched = true;
        }
    }

    // Ensure a chat exists in the list, otherwise refetch
    function upsertChatFromChatListEvent(payload: any) {
        // Expected payload: { conversation_id, message: {...}, unread_count? }
        const conversationId = payload?.conversation_id || payload?.conversationId || payload?.id;
        const message: Message | undefined = payload?.message;
        if (!conversationId) return;

        const existing = state.chats.find(c => c.id === Number(conversationId));
        if (existing) {
            if (message) {
                existing.last_message = message;
                existing.updated_at = message.created_at;
            }
            // If current chat is not open, bump unread
            if (!state.currentChat || state.currentChat.id !== Number(conversationId)) {
                existing.unread_count = (existing.unread_count || 0) + 1;
            }
        } else {
            // We don't know full chat details; safest to refresh list
            fetchChats();
        }
    }

    // Empty list, page mode, nothing pending.
    function resetList() {
        listGeneration++;
        state.messages = [];
        state.seenMessageIds?.clear();
        messagePagination.currentPage = 1;
        messagePagination.lastPage = 1;
        messagePagination.isLoadingPage = false;
        messagePagination.hasMore = true;
        pageRequest = null;
        windowState.cursorPaging = false;
        windowState.browsing = false;
        windowState.missed = [];
        liveArrivals = [];
    }

    // Set current chat and load messages
    function setCurrentChat(chat: Chat) {
        state.currentChat = chat;
        resetList();
        // Fetch first page
        void fetchConversationMessages(chat.uuid, 1)
        // Mark chat as read
        if (chat.unread_count > 0) {
            markChatAsRead(chat.id);
        }
    }

    // Mark chat as read
    async function markChatAsRead(conversationId: number) {
        try {
            const response = await ChatService.chatRead(conversationId);
            if (response.success) {
                // Update unread count in the chat
                const chat = state.chats.find(c => c.id === conversationId);
                if (chat) {
                    chat.unread_count = 0;
                }
            }
        } catch (error) {
            console.error('Failed to mark chat as read:', error);
        }
    }

    // Fetch paginated messages
    async function fetchConversationMessages(conversationUuid: string, page: number) {
        const key = `${conversationUuid}#${page}`

        while (pageRequest) {
            // Precies deze pagina loopt al: haak aan in plaats van meteen niets te
            // doen. `setCurrentChat` start pagina 1 zonder te wachten, en een
            // `await` hierop moet de berichten opleveren en niet een lege lijst.
            if (pageRequest.key === key) return pageRequest.promise
            // Een andere pagina wacht op zijn beurt. Meteen het antwoord van die
            // lopende aanvraag teruggeven zou betekenen dat deze pagina nooit
            // wordt opgehaald - precies wat de gebruiker bij terugscrollen vraagt.
            await pageRequest.promise
        }

        const request = { key, promise: loadPage(conversationUuid, page) }
        pageRequest = request
        try {
            await request.promise
        } finally {
            // Alleen opruimen als deze aanvraag nog de lopende is: wissel je van
            // gesprek, dan staat hier al de aanvraag van het nieuwe gesprek.
            if (pageRequest === request) pageRequest = null
        }
    }

    async function loadPage(conversationUuid: string, page: number) {
        messagePagination.isLoadingPage = true;
        const generation = listGeneration
        try {
            const res = await ChatService.getMessages(conversationUuid, page)
            // Wissel je van gesprek terwijl dit loopt, of sluit je het, dan hoort
            // deze pagina bij het vorige gesprek en mag hij nergens meer in
            // belanden - ook niet als er nu helemaal geen gesprek meer openstaat.
            // Zelfde als de lijst intussen vervangen is (venster, naar nieuwste).
            if (state.currentChat?.uuid !== conversationUuid || generation !== listGeneration) return
            if (res.success && res.data) {
                const pageMsgs: Message[] = Array.isArray(res.data.data) ? res.data.data as unknown as Message[] : []
                // API returns newest-first; prepend to maintain ascending order
                // Ensure no duplicates via seenMessageIds
                const filtered = pageMsgs.filter((m: Message) => !state.seenMessageIds?.has(m.id))
                filtered.forEach((m: Message) => state.seenMessageIds?.add(m.id))
                // Older page -> prepend
                state.messages = [...filtered.reverse(), ...state.messages]
                // Update pagination
                const meta = (res.data as any).meta || {}
                messagePagination.currentPage = meta.current_page || page
                messagePagination.lastPage = meta.last_page || page
                messagePagination.hasMore = messagePagination.currentPage < messagePagination.lastPage
            }
        } catch (e) {
            console.error('Failed to fetch conversation messages', e)
        } finally {
            messagePagination.isLoadingPage = false
        }
    }

    // De detailpagina hangt zijn infinite scroll hieraan op, zodat hij stopt met
    // vragen zodra de oudste pagina binnen is.
    const hasMoreMessages = computed(() => messagePagination.hasMore)

    async function loadOlderMessages() {
        if (!state.currentChat || !messagePagination.hasMore) return
        if (windowState.cursorPaging) {
            const oldest = state.messages[0]
            const result = oldest && await fetchWindow({ before: oldest.id })
            if (!result) return
            state.messages = [...unseen(result.messages), ...state.messages]
            messagePagination.hasMore = result.meta.has_older
            return
        }
        const nextPage = (messagePagination.currentPage || 1) + 1
        await fetchConversationMessages(state.currentChat.uuid, nextPage)
    }

    // One stretch of the conversation, oldest first. Null when it failed or the list
    // was replaced (or the conversation closed) while it loaded.
    async function fetchWindow(cursor: { around: number } | { before: number } | { after: number }) {
        const chat = state.currentChat
        if (!chat) return null
        const generation = listGeneration
        const res = await ChatService.getMessageWindow(chat.uuid, cursor)
        if (generation !== listGeneration || state.currentChat?.uuid !== chat.uuid || !res.success || !res.data) return null
        return { messages: [...res.data.data].reverse(), meta: res.data.meta }
    }

    // The messages not in the list yet, marked as seen.
    function unseen(messages: Message[]): Message[] {
        const fresh = messages.filter(m => !state.seenMessageIds?.has(m.id))
        fresh.forEach(m => state.seenMessageIds?.add(m.id))
        return fresh
    }

    // Replace the list with the stretch around a message. False if it could not be loaded.
    async function loadAround(messageId: number): Promise<boolean> {
        const arrivalsBefore = liveArrivals.length
        const result = await fetchWindow({ around: messageId })
        if (!result) return false
        listGeneration++
        pageRequest = null
        state.seenMessageIds?.clear()
        state.messages = unseen(result.messages)
        messagePagination.hasMore = result.meta.has_older
        windowState.cursorPaging = true
        // A live message that came in meanwhile landed in the list this answer replaced.
        // Browsing counts it from here; at now, ask for what came after.
        windowState.missed = liveArrivals.slice(arrivalsBefore).filter(id => !state.seenMessageIds?.has(id))
        windowState.browsing = result.meta.has_newer || windowState.missed.length > 0
        if (!result.meta.has_newer && windowState.browsing) await loadNewerMessages()
        return true
    }

    // While browsing: the next stretch after the newest loaded message. Once that
    // reaches now, browsing ends and the live messages that waited are appended.
    async function loadNewerMessages() {
        while (windowState.browsing) {
            const arrivalsBefore = liveArrivals.length
            const newest = state.messages[state.messages.length - 1]
            const result = newest && await fetchWindow({ after: newest.id })
            if (!result) return
            state.messages = [...state.messages, ...unseen(result.messages)]
            if (result.meta.has_newer) return
            // At now, unless a message came in while this loaded: then once more, so it
            // lands in order and as the server has it.
            if (liveArrivals.length === arrivalsBefore) {
                windowState.browsing = false
                windowState.missed = []
            }
        }
    }

    // Back to the newest page, in page mode.
    async function jumpToLatest() {
        if (!state.currentChat) return
        resetList()
        await fetchConversationMessages(state.currentChat.uuid, 1)
    }

    const isBrowsing = computed(() => windowState.browsing)
    const missedCount = computed(() => windowState.missed.length)

    // Shared success path of a send: insert the returned message and update the chat.
    function applySentResponse(conversationId: number, response: { success: boolean; message: string; data?: any }): SendResult {
        if (!(response.success && response.data)) {
            return { error: response.message || "" };
        }
        const newMessage: Message = {
            ...response.data.data,
            sender_id: currentUserId.value || response.data.data.sender_id,
        };

        // A slow upload can finish after the user opened another conversation.
        if (state.currentChat?.id === conversationId && !state.seenMessageIds?.has(newMessage.id)) {
            state.seenMessageIds?.add(newMessage.id);
            state.messages.push(newMessage);
        }

        const chat = state.chats.find(c => c.id === conversationId);
        if (chat) {
            chat.last_message = newMessage;
            chat.updated_at = newMessage.created_at;
        }

        return { message: newMessage };
    }

    // Send message. `messageId` edits that message, `replyToId` quotes another one.
    async function sendMessage(conversationId: number, message: string, attachments?: any[], messageId?: number, replyToId?: number): Promise<SendResult | undefined> {
        if (!message.trim() && (!attachments || attachments.length === 0)) return;

        try {
            const response = await ChatService.sendMessage(conversationId, message, attachments, messageId, replyToId);

            return applySentResponse(conversationId, response);
        } catch (error) {
            console.error('Failed to send message', error);
            return { error: "" };
        }
    }

    // Send a recorded voice note
    async function sendVoiceNote(conversationId: number, file: File, replyToId?: number): Promise<SendResult> {
        try {
            return applySentResponse(conversationId, await ChatService.sendVoiceNote(conversationId, file, replyToId));
        } catch (error) {
            console.error('Failed to send voice note', error);
            return { error: "" };
        }
    }

    // Put the server's reactions (and its preview) on a loaded message. A reaction is no new message:
    // the chat list and the unread count stay as they are.
    function applyReactions(message: Partial<Message> | undefined) {
        const index = state.messages.findIndex(msg => msg.id === Number(message?.id));
        if (index === -1) return;
        const existing = state.messages[index];
        state.messages[index] = {
            ...existing,
            preview: message?.preview ?? existing.preview,
            ...(Array.isArray(message?.reactions) ? { reactions: message.reactions } : {}),
        };
    }

    // Toggle my reaction. Returns `{ error }` with the server's text ("" for none) when it fails.
    async function toggleReaction(messageId: number, emoji: string): Promise<{ error?: string }> {
        const response = await ChatService.toggleReaction(messageId, emoji);
        if (!response.success) return { error: response.message };
        applyReactions(response.data);
        return {};
    }

    // Send typing indicator
    async function sendTypingIndicator(conversationId: number) {
        // Cancel previous in-flight typing request
        if (typingAbortController) {
            try { typingAbortController.abort() } catch { }
        }
        typingAbortController = new AbortController()

        // Debounce subsequent calls within 400ms window
        if (typingDebounceTimer) {
            clearTimeout(typingDebounceTimer)
        }
        typingDebounceTimer = window.setTimeout(async () => {
            try {
                await ChatService.typing(conversationId, { signal: typingAbortController?.signal })
            } catch (error) {
                // Ignore abort errors, log others
                if ((error as any)?.name !== 'AbortError') {
                    console.error('Failed to send typing indicator:', error)
                }
            }
        }, 400)
    }

    // Leave the chat
    async function leaveChat(conversationId: number) {
        try {
            return await ChatService.leave(conversationId);
        } catch (error) {
            console.error('Failed to leave the chat:', error)
        }
    }

    // Delete message
    async function deleteMessage(conversationId: number, message_id: number) {
        try {
            await ChatService.deleteMessage(conversationId, message_id);
        } catch (error) {
            state.error = error;
        }
    }

    // Handle real-time message received
    function handleMessageReceived(conversationId: number, message: Message) {
        const isOpen = state.currentChat?.id === Number(conversationId);
        if (isOpen) liveArrivals.push(message.id);
        // Add message to current messages if viewing this chat (avoid duplicates)
        if (!state.seenMessageIds?.has(message.id)) {
            if (isOpen && windowState.browsing) {
                // Not seen yet on purpose: the stretch that reaches now must still take it.
                if (!windowState.missed.includes(message.id)) windowState.missed.push(message.id);
            } else {
                state.seenMessageIds?.add(message.id);
                if (isOpen) state.messages.push(message);
            }
        } else if (state.messages.findIndex(msg => msg.id === message.id) !== -1) {
            const existingIndex = state.messages.findIndex(msg => msg.id === message.id);
            state.messages[existingIndex] = { ...state.messages[existingIndex], ...message };
        }

        // Update chat's last message
        const chat = state.chats.find(c => c.id === Number(conversationId));
        if (chat) {
            chat.last_message = message;
            chat.updated_at = message.created_at;
            if (!state.currentChat || state.currentChat.id !== Number(conversationId)) {
                chat.unread_count += 1;
            }

            markChatAsRead(chat.id);
        } else {
            // If chat not in list, refresh chats
            fetchChats();
        }
    }

    // Handle message read event
    function handleMessageRead(conversationId: number) {
        const chat = state.chats.find(c => c.id === Number(conversationId));
        if (chat) {
            chat.unread_count = 0;
        }
    }

    // Handle user typing event
    function handleUserTyping(conversationId: number, userId: number) {
        // Ignore typing events from current user
        if (currentUserId.value && Number(userId) === Number(currentUserId.value)) {
            return;
        }
        if (state.currentChat?.id === Number(conversationId)) {
            state.typingUsers.add(userId);

            // Remove typing indicator after 3 seconds
            setTimeout(() => {
                state.typingUsers.delete(userId);
            }, 3000);
        }
    }

    // Clear current chat
    function clearCurrentChat() {
        state.currentChat = null;
        state.messages = [];
        state.typingUsers.clear();
        listGeneration++;
        windowState.cursorPaging = false;
        windowState.browsing = false;
        windowState.missed = [];
        liveArrivals = [];
        // Zonder dit filtert het volgende gesprek berichten weg die toevallig
        // dezelfde id hebben als iets dat je hier al gezien had.
        state.seenMessageIds?.clear();
    }

    // Get chat by ID
    function getChatById(id: number): Chat | undefined {
        return state.chats.find(chat => chat.id === Number(id));
    }

    // Get participant name (excluding current user)
    function getParticipantName(chat: Chat | null): string {
        if (!chat) return '';

        if (chat.type === 'group') return chat.title;

        const me = currentUserId.value;
        if (!me) return chat.title;

        const participant = chat.participants.find(p => p.id !== me);
        return participant ? participant.name : chat.title;
    }

    // Get participant avatar
    function getParticipantAvatar(chat: Chat | null): string | null {
        if (!chat) return null;

        return chat.avatar;
    }

    // Check if user is typing
    function isUserTyping(userId: number): boolean {
        return state.typingUsers.has(userId);
    }

    // Check if current user is sender
    function isCurrentUserSender(message: Message): boolean {
        return message.sender_id === currentUserId.value;
    }

    // Compare update and create times
    function isEdited(message: Message): boolean {
        const date1 = new Date(message.created_at);
        const date2 = new Date(message.updated_at);

        return (date2.getTime() - date1.getTime()) / 1000 >= 2;
    }

    // Helpers to consume raw Pusher payloads safely
    function normalizePusherMessage(payload: any): { conversationId: number; message: Message } | null {
        const conversationId = payload?.conversationId || payload?.conversation_id || payload?.id;
        const raw = payload?.message || payload?.data || payload;
        if (!conversationId || !raw) return null;
        const normalized: Message = {
            id: Number(raw.id),
            message: String(raw.message ?? raw.text ?? ''),
            sender_id: Number(raw.sender_id ?? raw.user_id ?? raw.senderId ?? 0),
            created_at: String(raw.created_at ?? raw.timestamp ?? new Date().toISOString()),
            updated_at: String(raw.updated_at ?? raw.timestamp ?? new Date().toISOString()),
            deleted_at: raw.deleted_at ? String(raw.deleted_at) : null,
            attachments: Array.isArray(raw.attachments) ? raw.attachments : (raw.attachments ? [raw.attachments] : []),
            // Without it a reviewed check-in arriving live shows as plain text until reload.
            metadata: raw.metadata ?? null,
            preview: raw.preview,
            reply_to: raw.reply_to ?? null,
            reactions: Array.isArray(raw.reactions) ? raw.reactions : [],
        };
        return { conversationId: Number(conversationId), message: normalized };
    }

    function onPusherMessageSent(payload: any) {
        const event = normalizePusherMessage(payload);
        if (event) handleMessageReceived(event.conversationId, event.message);
    }

    // An edit is no new message: update it where it is shown, never add it.
    function onPusherMessageEdited(payload: any) {
        const event = normalizePusherMessage(payload);
        if (!event) return;
        const index = state.messages.findIndex(msg => msg.id === event.message.id);
        if (index !== -1) state.messages[index] = { ...state.messages[index], ...event.message };
        const chat = state.chats.find(c => c.id === event.conversationId);
        if (chat?.last_message?.id === event.message.id) chat.last_message = event.message;
    }

    function onPusherMessageDeleted(payload: any) {
        if (!payload?.message?.id) return;

        const existingIndex = state.messages.findIndex(msg => msg.id === Number(payload.message.id));
        state.messages[existingIndex] = { ...state.messages[existingIndex], ...payload.message };
    }

    function onPusherMessageReacted(payload: any) {
        applyReactions(payload?.message);
    }

    function onPusherMessageRead(payload: any) {
        const conversationId = payload?.conversation_id || payload?.conversationId || payload?.id;
        if (!conversationId) return;
        handleMessageRead(Number(conversationId));
    }

    function onPusherUserTyping(payload: any) {
        const conversationId = payload?.conversationId || payload?.conversation_id || payload?.id;
        const userId = payload?.senderId || payload?.user_id || payload?.sender_id;
        if (!conversationId || !userId) return;
        handleUserTyping(Number(conversationId), Number(userId));
    }

    return {
        ...toRefs(state),
        // Computed
        unreadChatsCount,
        hasMoreMessages,
        isBrowsing,
        missedCount,
        // Methods
        fetchChats,
        setCurrentChat,
        markChatAsRead,
        sendMessage,
        sendVoiceNote,
        sendTypingIndicator,
        leaveChat,
        handleMessageReceived,
        handleMessageRead,
        handleUserTyping,
        clearCurrentChat,
        getChatById,
        getParticipantName,
        getParticipantAvatar,
        isUserTyping,
        isCurrentUserSender,
        formatMessageTime,
        isEdited,
        deleteMessage,
        toggleReaction,
        // Upserts / Pusher helpers
        upsertChatFromChatListEvent,
        onPusherMessageSent,
        onPusherMessageEdited,
        onPusherMessageDeleted,
        onPusherMessageReacted,
        onPusherMessageRead,
        onPusherUserTyping,
        // Pagination
        fetchConversationMessages,
        loadOlderMessages,
        loadAround,
        loadNewerMessages,
        jumpToLatest,
    };
});
